import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getToken } from '../../services/tokenStore';
import {
  Server,
  Database,
  Clock,
  HardDrive,
  Users,
  FileText,
  Bell,
  RefreshCw,
  CheckCircle,
  XCircle,
} from 'lucide-react';

const API_BASE = '/api';

interface SystemStatus {
  status: string;
  database: string;
  uptime: number;
  memory: {
    rss: number;
    heapTotal: number;
    heapUsed: number;
    external: number;
  };
  nodeVersion: string;
  tableStats: {
    users: number;
    headaches: number;
    tensions: number;
    lloros: number;
    reminders: number;
    push_subscriptions: number;
  };
  timestamp: string;
}

export const AdminSystem: React.FC = () => {
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setIsLoading(true);
    setError('');

    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/admin/system/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();

      if (data.success) {
        setStatus(data.data);
      } else {
        setError('Error al obtener estado del sistema');
      }
    } catch (err) {
      console.error('Error loading system status:', err);
      setError('Error de conexión');
    } finally {
      setIsLoading(false);
    }
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);

    return parts.join(' ') || '< 1m';
  };

  const formatBytes = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text)]">Sistema</h1>
          <p className="text-[var(--color-text-muted)]">Estado y configuración del servidor</p>
        </div>
        <button
          onClick={loadStatus}
          className="btn btn-secondary w-full sm:w-auto"
        >
          <RefreshCw size={18} />
          Actualizar
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      {status && (
        <>
          {/* Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Server Status */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card p-5"
            >
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  status.status === 'online'
                    ? 'bg-green-100 dark:bg-green-900/30'
                    : 'bg-red-100 dark:bg-red-900/30'
                }`}>
                  {status.status === 'online' ? (
                    <CheckCircle size={24} className="text-green-500" />
                  ) : (
                    <XCircle size={24} className="text-red-500" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-[var(--color-text-muted)]">Estado</p>
                  <p className="text-lg font-bold text-[var(--color-text)] capitalize">{status.status}</p>
                </div>
              </div>
            </motion.div>

            {/* Database */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="card p-5"
            >
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  status.database === 'connected'
                    ? 'bg-green-100 dark:bg-green-900/30'
                    : 'bg-red-100 dark:bg-red-900/30'
                }`}>
                  <Database size={24} className={
                    status.database === 'connected' ? 'text-green-500' : 'text-red-500'
                  } />
                </div>
                <div>
                  <p className="text-sm text-[var(--color-text-muted)]">Base de datos</p>
                  <p className="text-lg font-bold text-[var(--color-text)] capitalize">
                    {status.database === 'connected' ? 'Conectada' : 'Error'}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Uptime */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="card p-5"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Clock size={24} className="text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-[var(--color-text-muted)]">Uptime</p>
                  <p className="text-lg font-bold text-[var(--color-text)]">{formatUptime(status.uptime)}</p>
                </div>
              </div>
            </motion.div>

            {/* Node Version */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="card p-5"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <Server size={24} className="text-purple-500" />
                </div>
                <div>
                  <p className="text-sm text-[var(--color-text-muted)]">Node.js</p>
                  <p className="text-lg font-bold text-[var(--color-text)]">{status.nodeVersion}</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Memory Usage */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="card p-6"
          >
            <h2 className="text-lg font-semibold text-[var(--color-text)] mb-4 flex items-center gap-2">
              <HardDrive size={20} className="text-[var(--color-accent)]" />
              Uso de Memoria
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl bg-[var(--color-bg)]">
                <p className="text-sm text-[var(--color-text-muted)]">RSS</p>
                <p className="text-xl font-bold text-[var(--color-text)]">{formatBytes(status.memory.rss)}</p>
              </div>
              <div className="p-4 rounded-xl bg-[var(--color-bg)]">
                <p className="text-sm text-[var(--color-text-muted)]">Heap Total</p>
                <p className="text-xl font-bold text-[var(--color-text)]">{formatBytes(status.memory.heapTotal)}</p>
              </div>
              <div className="p-4 rounded-xl bg-[var(--color-bg)]">
                <p className="text-sm text-[var(--color-text-muted)]">Heap Usado</p>
                <p className="text-xl font-bold text-[var(--color-text)]">{formatBytes(status.memory.heapUsed)}</p>
              </div>
              <div className="p-4 rounded-xl bg-[var(--color-bg)]">
                <p className="text-sm text-[var(--color-text-muted)]">Externo</p>
                <p className="text-xl font-bold text-[var(--color-text)]">{formatBytes(status.memory.external)}</p>
              </div>
            </div>
          </motion.div>

          {/* Table Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="card p-6"
          >
            <h2 className="text-lg font-semibold text-[var(--color-text)] mb-4 flex items-center gap-2">
              <FileText size={20} className="text-[var(--color-accent)]" />
              Estadísticas de Tablas
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="p-4 rounded-xl bg-[var(--color-bg)] text-center">
                <Users size={24} className="mx-auto text-blue-500 mb-2" />
                <p className="text-2xl font-bold text-[var(--color-text)]">{status.tableStats.users}</p>
                <p className="text-xs text-[var(--color-text-muted)]">Usuarios</p>
              </div>
              <div className="p-4 rounded-xl bg-[var(--color-bg)] text-center">
                <span className="text-2xl">🤕</span>
                <p className="text-2xl font-bold text-[var(--color-text)]">{status.tableStats.headaches}</p>
                <p className="text-xs text-[var(--color-text-muted)]">Dolores</p>
              </div>
              <div className="p-4 rounded-xl bg-[var(--color-bg)] text-center">
                <span className="text-2xl">❤️</span>
                <p className="text-2xl font-bold text-[var(--color-text)]">{status.tableStats.tensions}</p>
                <p className="text-xs text-[var(--color-text-muted)]">Tensiones</p>
              </div>
              <div className="p-4 rounded-xl bg-[var(--color-bg)] text-center">
                <span className="text-2xl">💧</span>
                <p className="text-2xl font-bold text-[var(--color-text)]">{status.tableStats.lloros}</p>
                <p className="text-xs text-[var(--color-text-muted)]">Lloros</p>
              </div>
              <div className="p-4 rounded-xl bg-[var(--color-bg)] text-center">
                <Bell size={24} className="mx-auto text-yellow-500 mb-2" />
                <p className="text-2xl font-bold text-[var(--color-text)]">{status.tableStats.reminders}</p>
                <p className="text-xs text-[var(--color-text-muted)]">Recordatorios</p>
              </div>
              <div className="p-4 rounded-xl bg-[var(--color-bg)] text-center">
                <span className="text-2xl">📲</span>
                <p className="text-2xl font-bold text-[var(--color-text)]">{status.tableStats.push_subscriptions}</p>
                <p className="text-xs text-[var(--color-text-muted)]">Push Subs</p>
              </div>
            </div>
          </motion.div>

          {/* Last Update */}
          <div className="text-center text-sm text-[var(--color-text-muted)]">
            Última actualización: {new Date(status.timestamp).toLocaleString('es-ES')}
          </div>
        </>
      )}
    </div>
  );
};
